import React, { useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { showAlert } from '../../../utils/sweetAlertUtils';

const LifeMembersExport: React.FC = () => {
    const { exportLifeMembersReport, isLoading } = useMemberApi();

    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Reports', href: '#' },
        { label: 'Members', href: '#' },
        { label: 'New Life Members', href: '#' },
    ];

    const handleClear = () => {
        setFromDate('');
        setToDate('');
    };

    const handleExport = async () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            showAlert({
                title: 'Validation Error',
                text: 'From Date cannot be after To Date',
                icon: 'error',
            });
            return;
        }

        try {
            const response = await exportLifeMembersReport(
                fromDate || undefined,
                toDate || undefined,
            );

            if (response?.data) {
                window.open(response.data, '_blank');
                showAlert({
                    title: 'Export Successful',
                    text: 'Your Life Members report has been generated and will download shortly.',
                    icon: 'success',
                });
            }
        } catch (error) {
            console.error('Error exporting Life Members report:', error);
            showAlert({
                title: 'Export Failed',
                text: 'An error occurred while generating the export. Please try again.',
                icon: 'error',
            });
        }
    };

    return (
        <AdminLayout
            activeMenuItem="/admin/reports"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Admin']}
            requiredPermission="Reports.LifeMembers.View"
        >
            <Card
                title="New Life Members Export"
                subtitle="Export members that transitioned to Life or Dedicated Life membership within the selected date range. Leave dates empty to export all life members."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                        id="fromDate"
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="Select From Date"
                        allowFutureDates={false}
                    />
                    <FormField
                        id="toDate"
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="Select To Date"
                        allowFutureDates={false}
                    />
                </div>
                <div className="mt-6 flex gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClear}
                        disabled={isLoading}
                    >
                        Clear
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleExport}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Generating...' : 'Export'}
                    </Button>
                </div>
            </Card>
        </AdminLayout>
    );
};

export default LifeMembersExport;
